import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Chip,
} from '@mui/material';
import {
  PersonAdd,
  Edit,
  Delete,
  EventBusy,
  PersonOff,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

interface Activity {
  id: string;
  type: 'create' | 'update' | 'delete' | 'absence' | 'assignment';
  description: string;
  timestamp: Date;
  user?: string;
}

interface RecentActivityProps {
  activities: Activity[];
  loading?: boolean;
}

export function RecentActivity({ activities, loading }: RecentActivityProps) {
  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'create':
        return <PersonAdd />;
      case 'update':
        return <Edit />;
      case 'delete':
        return <Delete />;
      case 'absence':
        return <EventBusy />;
      case 'assignment':
        return <PersonOff />;
      default:
        return <Edit />;
    }
  };

  const getActivityColor = (type: Activity['type']) => {
    switch (type) {
      case 'create':
        return 'success';
      case 'update':
        return 'info';
      case 'delete':
        return 'error';
      case 'absence':
        return 'warning';
      case 'assignment':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader title="Recent Activity" />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardHeader title="Recent Activity" />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No recent activity
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Recent Activity" />
      <CardContent sx={{ pt: 0 }}>
        <List>
          {activities.map((activity, index) => (
            <ListItem
              key={activity.id}
              divider={index < activities.length - 1}
              sx={{ px: 0 }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: `${getActivityColor(activity.type)}.light`,
                    color: `${getActivityColor(activity.type)}.main`,
                  }}
                >
                  {getActivityIcon(activity.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={activity.description}
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                    {activity.user && ` • ${activity.user}`}
                  </Typography>
                }
              />
              <Chip
                label={activity.type}
                size="small"
                color={getActivityColor(activity.type) as any}
                variant="outlined"
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
